import React, { useState, useEffect, useContext, useRef } from 'react';
import { PatientIDContext } from '../../../Contexts';
import { clearRecording, pauseRecording, resumeRecording, startRecording, stopRecording } from '../../../util/recordingAPI';
import { deleteTrial } from '../../../util/trialApi';
import { normalizeTrialId } from '../../../util/trialId';
import { recorderHeaders } from '../../../util/sessionTransfer';
import { useSharedAllowRecord, useSharedCameraStream, useSharedCalibrationID, useSharedReplayAssessment, useFilename, useSaveButton, useSharedResolution, useSharedTrialData } from '../../SharedHooks';
import StreamCamera from '../video/StreamCamera';
import RecordPatient from '../RecordPatient';
import GlobalOverlayPortal from '../../modals/GlobalOverlayPortal';
import { TailSpin } from 'react-loader-spinner';


const RECORDING_INIT_TIMEOUT_MS = 60000;

function DisplayButtons(props) {
    const { title, actions, setActions, deferTimerUntilInit, isInitializing, setInitializing, setInitError } = props;
    const { record, resumeRecord } = actions;
    const patientID = useContext(PatientIDContext);
    const [filename, setFilename] = useState("");
    const { cameraStream, setcameraStream } = useSharedCameraStream();
    const { SaveButton, setSaveButton } = useSaveButton();
    const { trialData, setTrialData } = useSharedTrialData();
    const { allowRecord, setAllowRecord } = useSharedAllowRecord();
    const { resolution } = useSharedResolution();
    const { setReplayAssessment } = useSharedReplayAssessment();
    const { calibrationID, setcalibrationID } = useSharedCalibrationID();
    const uploadIntervalRef = useRef(null);
    const [processState, setProcessState] = useState({
        isLoading: false,
        downloadProgress: '0/0',
        uploadProgress: '0/0',
        totalCameras: 0,
        completedDownloads: 0,
        completedUploads: 0,
        downloadDone: false,
        uploadDone: false,
      });

    const boxStyles = {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '150px', 
        padding: '16px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 0 16px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      };
    const buttonStyle = {
        backgroundColor: '#80bfff',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',

    };

    const startProcess = async () => {
        setProcessState(prevState => ({ ...prevState, isLoading: true }));
        const totalCamerasRes = await fetch(`${window.RECORDING_URL}/api/total-cameras`, {
          headers: recorderHeaders(),
        }).then(res => res.json());
        setProcessState(prevState => ({
          ...prevState,
          totalCameras: totalCamerasRes.tot_cams,
          downloadProgress: `0/${totalCamerasRes.tot_cams}`,
          uploadProgress: `0/${totalCamerasRes.tot_cams}`,
        }));
    
        // Start the download process and then the upload process
        startDownloadProcess(totalCamerasRes.tot_cams);
      };
    
      const startDownloadProcess = async (totalCameras) => {
        // Initiate the download process without waiting for it to complete
        fetch(`${window.RECORDING_URL}/api/finish-session`, { headers: recorderHeaders() }).then(response => {
          // Even if you don't wait here, ensure basic error handling
          console.log('Download session initiated');
        }).catch(error => {
          console.error('Error initiating download session:', error);
        });
      
        // Immediately start polling for download progress
        const interval = setInterval(() => {
          fetch(`${window.RECORDING_URL}/api/update-download`, { headers: recorderHeaders() })
            .then(response => response.json())
            .then(data => {
              const { downloaded } = data;
      
              setProcessState(prevState => ({
                ...prevState,
                completedDownloads: downloaded,
                downloadProgress: `${downloaded}/${totalCameras}`,
              }));
      
              if (downloaded >= totalCameras) {
                clearInterval(interval);
                setProcessState(prevState => ({
                  ...prevState,
                  downloadDone: true,
                  downloadProgress: 'Done',
                }));
      
              }
            })
            .catch(error => {
              console.error('Error fetching download progress:', error);
              clearInterval(interval); // Stop polling if there's an error
            });
        }, 3000); // Adjust the polling interval as needed
      };
      
    
      const startUploadProcess = (totalCameras) => {
        // Clear any existing interval to prevent duplicates
        if (uploadIntervalRef.current) {
          clearInterval(uploadIntervalRef.current);
        }
      
        uploadIntervalRef.current = setInterval(() => {
          fetch(`${window.RECORDING_URL}/api/update-upload`, { headers: recorderHeaders() })
            .then(response => response.json())
            .then(data => {
              const { trial_id, uploaded, total} = data;
              // data from the raspberry pi is ={'trial_id': trial_id, 'uploaded': count, 'total': len(camgens)}
      
              setProcessState(prevState => ({
                ...prevState,
                completedUploads: uploaded,
                uploadProgress: `trial ${trial_id}: ${uploaded}/${totalCameras}`,
              }));
      
              if (uploaded >= totalCameras) {
                clearInterval(uploadIntervalRef.current);
                setProcessState(prevState => ({
                  ...prevState,
                  uploadDone: true,
                  uploadProgress: `${trial_id}: Done`,
                  isLoading: false,
                }));
              }
            })
            .catch(error => {
              console.error('Error fetching upload progress:', error);
              clearInterval(uploadIntervalRef.current);
            });
        }, 3000); // Adjust the polling interval as needed
      };
      
      // Clear the interval when the component unmounts
      useEffect(() => {
        return () => {
          if (uploadIntervalRef.current) {
            clearInterval(uploadIntervalRef.current);
          }
        };
      }, []);
    
      // UseEffect for handling the process click, simplified for brevity
      useEffect(() => {
        if (processState.isLoading) {
          // If downloads are done but uploads haven't started
          if (processState.downloadDone && !processState.uploadDone) {
            startUploadProcess(processState.totalCameras);
          }
        }
      }, [processState]);

    const LoadingOverlay = ({ message }) => (
        <GlobalOverlayPortal>
          <div className="app-global-overlay--fullscreen-backdrop">
            <div>
              <TailSpin color="#ffffff" />
              <p>{message}</p>
            </div>
          </div>
        </GlobalOverlayPortal>
      );
      

    // const fetchUpdateDownload = (totalCamerasParam) => {
    //     const interval = setInterval(() => {
    //       fetch(`${window.RECORDING_URL}/api/update-download`)
    //         .then(response => response.json())
    //         .then(data => {
    //           setCompletedDownloads(data.downloaded);
    //           setDownloadProgress(`${data.downloaded}/${totalCamerasParam}`);
    //           if (completedDownloads === totalCamerasParam) {
    //             setDownloadProgress('Done');
    //             clearInterval(interval); // Stop checking for updates
    //           }
    //         })
    //         .catch(error => console.error('Error fetching download progress:', error));
    //     }, 1500);
      
    //     return () => clearInterval(interval); // Ensure we have a way to clear this interval when needed
    //   };
    
    //   const fetchUpdateUpload = (totalCamerasParam) => {
    //     const interval = setInterval(() => {
    //       fetch(`${window.RECORDING_URL}/api/update-upload`)
    //         .then(response => response.json())
    //         .then(data => {
    //           setCompletedUploads(data.uploaded);
    //           setUploadProgress(`${data.uploaded}/${totalCamerasParam}`);
    //           if (completedUploads === totalCamerasParam) {
    //             setUploadProgress('Done');
    //             setShowPopupUpload(false)
    //             setCompletedUploads(0)
    //             setIsLoading(false)
    //             setShowPopupDownload(false)
    //             setCompletedDownloads(0)
    //             clearInterval(interval);
    //           }
    //         })
    //         .catch(error => console.error('Error fetching download progress:', error));
    //     }, 1500);
      
    //     return () => clearInterval(interval); // Ensure we have a way to clear this interval when needed
    //   };
      
    //   const fetchTotalCameras = () => {
    //     fetch(`${window.RECORDING_URL}/api/total-cameras`)
    //       .then(res => res.json())
    //       .then(data => {
    //         setDownloadProgress(`${completedDownloads}/${data.tot_cams}`);
    //         setTotalCameras(data.tot_cams);
    //         fetchUpdateDownload(data.tot_cams); // Pass totalCameras directly
    //         fetchUpdateUpload(data.tot_cams);

    //       })
    //       .catch(err => console.error(err));
    //   };
  
    const handleProcessClick = () => {
        if (processState.downloadDone) {
          window.location.reload();
          return;
        }

          setProcessState({
            isLoading: false,
            downloadProgress: '0/0',
            uploadProgress: '0/0',
            totalCameras: 0,
            completedDownloads: 0,
            completedUploads: 0,
            downloadDone: false,
            uploadDone: false,
        });

        startProcess();

        // setIsLoading(true);

        // setShowPopupDownload(true);
        // setShowPopupUpload(true);

        // fetchTotalCameras();

        // fetch(`${window.RECORDING_URL}/api/finish-session`)
        //   .then(response => response.json())
        //   .then(data => {
        //     if (data.status === 'success') {
        //     } else {
        //       console.error('Finish session failed:', data.reason);
        //     }
        //   })
        //   .catch(error => {
        //     console.error('Error finishing session:', error);
        //   });
      };

    const [displayButton, setDisplayButton] = useState(
            <button
                className={allowRecord ? "blue-button" : ""}
                onClick={() => {
                    if(allowRecord) toggleRecord();
                    setSaveButton(false);
                    setTrialData(trialData);
                }}
            >
                Start Recording
            </button>
    );

    const toggleRecord = () => {
        if (record) {
            setActions({ record: false });
            setAllowRecord(true);
            stopRecording();
            return;
        }
        if (deferTimerUntilInit && setInitializing) {
            if (isInitializing) return;
            setAllowRecord(false);
            setInitializing(true);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Initialization timed out')), RECORDING_INIT_TIMEOUT_MS)
            );
            Promise.race([
                startRecording(patientID, resolution, title, calibrationID),
                timeoutPromise,
            ])
                .then(res => {
                    if (res && typeof res === 'object' && res.error) {
                        throw new Error(res.error || 'Failed to start recording');
                    }
                    setActions({ record: true });
                    setInitializing(false);
                    const newId = normalizeTrialId(res);
                    if (newId) {
                        setTrialData(prev => ({ ...prev, [title]: { ...prev[title], id: newId } }));
                        setFilename(newId);
                    }
                })
                .catch(err => {
                    setInitializing(false);
                    setActions({ record: false });
                    setAllowRecord(true);
                    if (setInitError) {
                        setInitError(
                            err && err.message
                                ? err.message
                                : 'Unable to start recording; cameras failed to initialize. Please try again.'
                        );
                    }
                    stopRecording().catch(e => console.error('[Init failure] stopRecording:', e));
                });
            return;
        }
        setAllowRecord(false);
        setActions({ record: true });
        startRecording(patientID, resolution, title, calibrationID).then(res => {
            const newId = normalizeTrialId(res);
            if (newId) {
                setTrialData({ ...trialData, [title]: { ...trialData[title], id: newId } });
                setFilename(newId);
            }
        });
    };

    const replayVideo = () => setReplayAssessment(filename);

    useEffect(() => {
        let displayButton = null;
        const { record, resumeRecord } = actions;
        if(record) {
            displayButton = (
                <div>
                    <i style={{"color": "red"}} className="far fa-stop-circle fa-2x" onClick={toggleRecord}/>
                </div>
            )
        } else if(trialData[title]) {
            if(cameraStream && trialData[title].id) {
                console.log(trialData[title].id)
                displayButton = (
                  <div className="button-container">
                        <button
                            // gray out the start button if not allowed to record
                            disabled={processState.isLoading}
                            className={allowRecord ? "blue-button" : ""}
                            // prevent user from recording multiple assessments at a once
                            onClick={() => {
                                const trialId = normalizeTrialId(trialData[title].id);
                                setSaveButton(false);
                                setAllowRecord(false);
                                setActions({ resumeRecord: true, record: true });
                                // Clear RPI-side recording (no trial_id).
                                clearRecording().catch(err =>
                                    console.error("[Redo] clearRecording failed:", err)
                                );
                                // Start recording again via /api/start-record, same as the
                                // normal start recording button, but mark it as a redo so
                                // the backend can choose to reuse the existing id.
                                startRecording(patientID, resolution, title, calibrationID, {
                                    redo: true,
                                    trialId,
                                })
                                    .then(res => {
                                        const newId = normalizeTrialId(res) ?? trialId;
                                        if (newId) {
                                            setTrialData({
                                                ...trialData,
                                                [title]: { ...trialData[title], id: newId }
                                            });
                                            setFilename(newId);
                                        }
                                    })
                                    .catch(err =>
                                        console.error("[Redo] startRecording failed:", err)
                                    );

                            }}
                        >
                            Redo
                        </button>
                            <button 
                              onClick={handleProcessClick} 
                              disabled={processState.isLoading}
                              className="blue-button"
                              data-testid="upload-to-server-btn"
                            >
                              {processState.downloadDone ? "Start new recording" : (processState.isLoading ? "Uploading..." : "Upload to server")}
                            </button>
                   </div>
                )
            } else if(cameraStream) {
                displayButton = (
                    <button
                        className={allowRecord ? "blue-button" : ""}
                        disabled={deferTimerUntilInit && isInitializing}
                        onClick={() => {
                            if (allowRecord && !(deferTimerUntilInit && isInitializing)) toggleRecord();
                            setSaveButton(false);
                        }}
                    >
                        Start Recording
                    </button>
                )
            } else if(!cameraStream && trialData[title].id) {
                displayButton = (
                    <button 
                        className={allowRecord ? "blue-button" : ""}
                        onClick={replayVideo}
                    >
                        Play Video
                    </button>
                )
            } else if(!(cameraStream || trialData[title].id)) {
                displayButton = <button>Play Video</button>;
            }
        } else {
            displayButton = (
                <button
                    className={allowRecord ? "blue-button" : ""}
                    disabled={deferTimerUntilInit && isInitializing}
                    onClick={() => {
                        if (allowRecord && !(deferTimerUntilInit && isInitializing)) toggleRecord();
                        setSaveButton(false);
                        setTrialData(trialData);
                    }}
                >
                    Start Recording
                </button>
            );
        }
        setDisplayButton(displayButton);
    }, [actions, allowRecord, cameraStream, trialData, deferTimerUntilInit, isInitializing])

    return (
        <div>
            {processState.isLoading && <LoadingOverlay message="Videos are being processed..." />}
            {displayButton}
            {/* {processState.isLoading && (
                <div>

                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '5px',
                    zIndex: 1000,
                }}> 
                Download Progress: {processState.downloadProgress}
                </div>

                <div style={{
                    position: 'fixed',
                    bottom: '60px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '5px',
                    zIndex: 1000,
                }}>
                    Upload Progress: {processState.uploadProgress}
                </div>
                </div>
            )} */}
            {/* {showPopupDownload && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '5px',
                    zIndex: 1000,
                }}>
                    Download Progress: {downloadProgress}
                </div>
            )}
        {showPopupUpload && (
                <div style={{
                    position: 'fixed',
                    bottom: '60px',
                    right: '20px',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '5px',
                    zIndex: 1000, // Ensure it's above other content
                }}>
                    Upload Progress: {uploadProgress}
                </div>
            )} */}
        </div>
    );

}

export default DisplayButtons;